import React, { useEffect, useState } from 'react';
import { Tag, message, Empty, Pagination, Typography, Space, Grid } from 'antd';
import { fiscalDataService } from '../../services/api';
import ResponsiveTable from '../../components/Common/ResponsiveTable';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const ESTADOS = {
  stamped: { color: 'green', label: 'Timbrada' },
  draft: { color: 'default', label: 'Borrador' },
  cancelled: { color: 'red', label: 'Cancelada' },
  canceled: { color: 'red', label: 'Cancelada' },
};

const pesos = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(v || 0));

/**
 * Facturas del contribuyente, en SOLO LECTURA.
 *
 * Emitir y cancelar no están delegados —el límite de la delegación lo prohíbe— así
 * que aquí no hay ninguna acción que las produzca: es una vista para saber qué se
 * ha emitido, no un lugar desde donde facturar.
 */
const FacturasPanel = ({ empresa }) => {
  const screens = useBreakpoint();
  const esMovil = !screens.md;
  const companyId = empresa?.id;

  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [porPagina] = useState(20);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      if (!companyId) return;
      setCargando(true);
      try {
        const data = await fiscalDataService.getInvoices(companyId, {
          limit: porPagina,
          offset: (pagina - 1) * porPagina,
        });
        setLista(data?.invoices || []);
        setTotal(data?.total || 0);
      } catch (err) {
        message.error(err?.response?.data?.error || 'No se pudieron cargar las facturas');
      } finally {
        setCargando(false);
      }
    };

    cargar();
  }, [companyId, pagina, porPagina]);

  const columns = [
    {
      title: 'Folio',
      key: 'folio',
      render: (_, f) => (
        <Space direction="vertical" size={0}>
          <Text strong>{[f.serie, f.folio].filter(Boolean).join(' ') || '—'}</Text>
          {f.uuid && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {String(f.uuid).slice(0, 8)}…
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Receptor',
      key: 'receptor',
      render: (_, f) => f.receiver?.tax_name || f.receiver?.tax_id || '—',
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      render: (v, f) => String(v || f.created_at || '').slice(0, 10) || '—',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (v) => pesos(v),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const e = ESTADOS[s] || { color: 'default', label: s || '—' };
        return <Tag color={e.color}>{e.label}</Tag>;
      },
    },
  ];

  return (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Consulta de lo emitido. Emitir y cancelar se hacen desde la cuenta del
        contribuyente, no desde aquí.
      </Text>

      <ResponsiveTable
        rowKey="id"
        loading={cargando}
        dataSource={lista}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: true }}
        locale={{
          emptyText: <Empty description="Sin facturas emitidas" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
        }}
      />

      {total > porPagina && (
        <Pagination
          current={pagina}
          pageSize={porPagina}
          total={total}
          onChange={setPagina}
          simple={esMovil}
          showSizeChanger={false}
          style={{ marginTop: 12, textAlign: 'right' }}
        />
      )}
    </>
  );
};

export default FacturasPanel;
